import { ConsultDto } from 'app/models/generated'
import { logger } from 'app/logging/Logger'
import { subscribeGraphQl } from 'app/api/client/graphqlClient'
import {
    onCreateConsultDtoSubscription,
    onUpdateConsultDtoSubscription,
    onCreateConsultTemplateDto,
    onUpdateConsultTemplateDto,
    onCreateConsultAdditionalTemplateDto,
    onUpdateConsultAdditionalTemplateDto,
    onCreateConsultPatientDto,
    onUpdateConsultPatientDto,
} from 'app/graphql/customQueries'

export interface SubscriptionHandle {
    unsubscribe: () => void
}

/**
 * Helper to create a GraphQL subscription and return a SubscriptionHandle.
 * Centralises the subscription setup, through subscribeGraphQl.
 */
function createSubscription<T>(
    query: string,
    variables: Record<string, unknown>,
    operationName: string,
    callback: (data: T) => void,
    errorAction: string,
    onPoke?: () => void
): SubscriptionHandle {
    console.error(
        '[DIAG] createSubscription:',
        operationName,
        'filter:',
        JSON.stringify(variables)
    )
    const sub = subscribeGraphQl(query, variables, {
        next: ({ data }) => {
            const payload = data?.[operationName] as T | undefined

            console.error(
                '[DIAG] subscription event received:',
                operationName,
                'hasPayload:',
                !!payload,
                'id:',
                (payload as { id?: string } | undefined)?.id?.substring(0, 8)
            )
            if (payload) {
                callback(payload)
            } else if (onPoke) {
                // Owner-auth subscriptions deliver a NULL payload for events
                // triggered by IAM-mode mutations (the backend note pipeline),
                // so the event fires but carries no data. Treat it as a
                // "something changed" poke: the caller refetches via its own
                // owner-authed query (see ACA-126 subscription-delivery fix).
                onPoke()
            }
        },
        error: (error: unknown) => {
            console.error('[DIAG] subscription ERROR:', operationName, error)
            logger.error(
                `Subscription error: ${errorAction}`,
                error instanceof Error ? error : new Error(String(error)),
                { action: errorAction }
            )
        },
    })

    return { unsubscribe: () => sub.unsubscribe() }
}

/**
 * Manages GraphQL subscription lifecycle for consult-related events.
 * Returns raw DTO events via callbacks — no domain model mapping.
 *
 * Each method returns a SubscriptionHandle with `unsubscribe()` for cleanup.
 */
export const consultSubscriptionManager = {
    /** Subscribe to new consults created for an owner */
    onConsultCreated(
        owner: string,
        callback: (dto: ConsultDto) => void
    ): SubscriptionHandle {
        return createSubscription<ConsultDto>(
            onCreateConsultDtoSubscription,
            { owner },
            'onCreateConsultDto',
            callback,
            'consult_create_subscription_error'
        )
    },

    /** Subscribe to updated consults for an owner */
    onConsultUpdated(
        owner: string,
        callback: (dto: ConsultDto) => void,
        onPoke?: () => void
    ): SubscriptionHandle {
        return createSubscription<ConsultDto>(
            onUpdateConsultDtoSubscription,
            { owner },
            'onUpdateConsultDto',
            callback,
            'consult_update_subscription_error',
            onPoke
        )
    },

    /** Subscribe to updates for a specific consult by ID */
    onConsultUpdatedById(
        owner: string,
        consultId: string,
        callback: (dto: ConsultDto) => void
    ): SubscriptionHandle {
        return createSubscription<ConsultDto>(
            onUpdateConsultDtoSubscription,
            { owner, filter: { id: { eq: consultId } } },
            'onUpdateConsultDto',
            callback,
            'consult_update_by_id_subscription_error'
        )
    },

    /** Subscribe to template create/update for a consult */
    onTemplateChanged(
        owner: string,
        consultId: string,
        callback: () => void
    ): SubscriptionHandle {
        const filter = { owner, filter: { consultId: { eq: consultId } } }
        const createSub = createSubscription(
            onCreateConsultTemplateDto,
            filter,
            'onCreateConsultTemplateDto',
            () => callback(),
            'template_create_subscription_error'
        )
        const updateSub = createSubscription(
            onUpdateConsultTemplateDto,
            filter,
            'onUpdateConsultTemplateDto',
            () => callback(),
            'template_update_subscription_error'
        )

        return {
            unsubscribe: () => {
                createSub.unsubscribe()
                updateSub.unsubscribe()
            },
        }
    },

    /** Subscribe to additional template create/update for a consult */
    onAdditionalTemplateChanged(
        owner: string,
        consultId: string,
        callback: () => void
    ): SubscriptionHandle {
        const filter = { owner, filter: { consultId: { eq: consultId } } }
        const createSub = createSubscription(
            onCreateConsultAdditionalTemplateDto,
            filter,
            'onCreateConsultAdditionalTemplateDto',
            () => callback(),
            'additional_template_create_subscription_error'
        )
        const updateSub = createSubscription(
            onUpdateConsultAdditionalTemplateDto,
            filter,
            'onUpdateConsultAdditionalTemplateDto',
            () => callback(),
            'additional_template_update_subscription_error'
        )

        return {
            unsubscribe: () => {
                createSub.unsubscribe()
                updateSub.unsubscribe()
            },
        }
    },

    /** Subscribe to patient create/update for a consult */
    onPatientChanged(
        owner: string,
        consultId: string,
        callback: () => void
    ): SubscriptionHandle {
        const filter = { owner, filter: { consultId: { eq: consultId } } }
        const createSub = createSubscription(
            onCreateConsultPatientDto,
            filter,
            'onCreateConsultPatientDto',
            () => callback(),
            'patient_create_subscription_error'
        )
        const updateSub = createSubscription(
            onUpdateConsultPatientDto,
            filter,
            'onUpdateConsultPatientDto',
            () => callback(),
            'patient_update_subscription_error'
        )

        return {
            unsubscribe: () => {
                createSub.unsubscribe()
                updateSub.unsubscribe()
            },
        }
    },

    /** Subscribe to new ConsultPatientDto associations for a patient */
    onPatientConsultCreated(
        owner: string,
        patientId: string,
        callback: (dto: { consultId: string }) => void
    ): SubscriptionHandle {
        return createSubscription<{ consultId: string }>(
            onCreateConsultPatientDto,
            { owner, filter: { patientId: { eq: patientId } } },
            'onCreateConsultPatientDto',
            callback,
            'patient_consult_create_subscription_error'
        )
    },

    /** Subscribe to updated ConsultPatientDto associations for a patient */
    onPatientConsultUpdated(
        owner: string,
        patientId: string,
        callback: (dto: { consultId: string }) => void
    ): SubscriptionHandle {
        return createSubscription<{ consultId: string }>(
            onUpdateConsultPatientDto,
            { owner, filter: { patientId: { eq: patientId } } },
            'onUpdateConsultPatientDto',
            callback,
            'patient_consult_update_subscription_error'
        )
    },
}
